import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Plus, Fuel, Zap, Wrench, Sparkles, IndianRupee, Gauge, HeartPulse,
  ClipboardList, AlertTriangle, ChevronRight, MessageSquare, ShieldCheck, Car,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useEntitlements } from "@/context/EntitlementsContext";
import ProCTA from "@/components/ProCTA";

const fmt = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n || 0);

export default function Dashboard() {
  const nav = useNavigate();
  const { user, hasPerm } = useAuth();
  const { flag } = useEntitlements();
  const [roll, setRoll] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [rr, vv] = await Promise.all([
          api.get(`/dashboard/roll-up${vehicleId ? `?vehicle_id=${vehicleId}` : ""}`),
          api.get("/vehicles"),
        ]);
        setRoll(rr.data);
        // Filter to assigned vehicles for non-admin drivers
        const assigned = user?.assigned_vehicle_ids || [];
        const list = (user?.is_admin || assigned.length === 0)
          ? vv.data
          : vv.data.filter((v) => assigned.includes(v.id));
        setVehicles(list);
      } catch { /* silent */ }
    })();
  }, [vehicleId, user]);

  useEffect(() => {
    // AI alerts (best-effort)
    if (!vehicles.length || !hasPerm("view_health")) return;
    const vid = vehicleId || vehicles[0]?.id;
    if (!vid || !flag("ai_insights")) return;
    api.get(`/ai/insights/${vid}`)
      .then((r) => {
        const d = r.data || {};
        const a = [];
        if (d.mileage_anomalies?.length) a.push({ level: "warn", text: `${d.mileage_anomalies.length} mileage anomaly detected` });
        if (d.repeated_repairs?.length)   a.push({ level: "warn", text: `Repeated repairs: ${d.repeated_repairs[0]?.title || "review timeline"}` });
        if (d.premature_wear?.length)     a.push({ level: "danger", text: `Premature wear on ${d.premature_wear[0]?.component || "a component"}` });
        setAlerts(a.slice(0, 3));
      })
      .catch(() => setAlerts([]));
  }, [vehicleId, vehicles, hasPerm, flag]);

  // Non-admin who can only log data → put "+ Log" front-and-centre (Item 2)
  const canOnlyLog = !user?.is_admin
    && (hasPerm("add_fuel") || hasPerm("add_charging") || hasPerm("log_service") || hasPerm("log_repair") || hasPerm("upload_invoice"))
    && !hasPerm("view_expenses");

  const spend = roll?.month_spend || { total: 0, fuel: 0, charging: 0, service: 0, repair: 0 };
  const health = roll?.vehicle_health?.score || null;

  const monthLabel = useMemo(() => {
    if (!roll?.period?.start) return "This month";
    return new Date(roll.period.start).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  }, [roll]);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header — city + vehicle + upgrade */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="ft-overline">RAYFUEL · {monthLabel.toUpperCase()}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none">
            Hello{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="dashboard-vehicle-select"
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            className="px-3 py-2 rounded-lg border border-[var(--border-line)] bg-[var(--bg-surface)] text-sm"
          >
            <option value="">All vehicles ({vehicles.length})</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.number}</option>)}
          </select>
          <ProCTA testid="dashboard-upgrade-cta" />
        </div>
      </div>

      {canOnlyLog ? (
        <DriverPrimaryLog />
      ) : (
        <>
          {/* Central primary + Log button */}
          <PrimaryLogRow />

          {/* Stat tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile
              testid="tile-spend"
              icon={IndianRupee} label="This month's spend"
              value={`₹${fmt(spend.total)}`}
              hint={`${vehicles.length} vehicle${vehicles.length === 1 ? "" : "s"} · ${fmt(roll?.month_km || 0)} km`}
            />
            <Tile
              testid="tile-costkm"
              icon={Gauge} label="Cost per km"
              value={roll?.cost_per_km ? `₹${roll.cost_per_km}` : "—"}
              hint="All spends ÷ km driven"
            />
            <Tile
              testid="tile-health"
              icon={HeartPulse} label="Vehicle health"
              value={health != null ? `${health}/100` : "—"}
              hint={health != null ? "Rolling AI score" : "Add records to unlock"}
            />
            <Tile
              testid="tile-nextservice"
              icon={Wrench} label="Next service"
              value={roll?.next_service ? `${fmt(roll.next_service.km_left)} km` : "—"}
              hint={roll?.next_service?.vehicle_number || "—"}
              intent={roll?.next_service?.overdue ? "danger" : "default"}
            />
          </div>

          {/* AI alerts */}
          {alerts.length > 0 && (
            <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/5 p-4" data-testid="dashboard-ai-alerts">
              <div className="ft-overline text-yellow-500 mb-2">AI ALERTS</div>
              <ul className="space-y-2 text-sm">
                {alerts.map((a, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <AlertTriangle size={14} className={a.level === "danger" ? "text-red-400 mt-0.5" : "text-yellow-400 mt-0.5"} />
                    <span>{a.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Expense split */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ExpenseCard label="Fuel + Charging" icon={Fuel} amount={spend.fuel + spend.charging} tone="orange" testid="expense-fuel" />
            <ExpenseCard label="Service"          icon={Wrench} amount={spend.service} tone="amber" testid="expense-service" />
            <ExpenseCard label="Repair"           icon={ShieldCheck} amount={spend.repair} tone="red" testid="expense-repair" />
          </div>

          {/* Recent timeline + Ask RayFuel */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-5" data-testid="dashboard-timeline">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="ft-overline">RECENT TIMELINE</p>
                  <h3 className="font-display text-xl font-bold">Last 10 events</h3>
                </div>
                <Link to="/insights" className="text-xs text-[#FF5A1F] hover:underline inline-flex items-center gap-1">
                  All insights <ChevronRight size={12} />
                </Link>
              </div>
              {(!roll?.recent_events || roll.recent_events.length === 0) ? (
                <p className="text-sm text-[var(--text-muted)]">No activity yet this month. Tap + Log to add your first refuel.</p>
              ) : (
                <ul className="divide-y divide-[var(--border-line)]">
                  {roll.recent_events.map((e, i) => (
                    <li key={i} className="py-2.5 flex items-center gap-3 text-sm">
                      <EventIcon kind={e.kind} />
                      <div className="flex-1 min-w-0">
                        <p className="truncate">{e.note || e.kind}</p>
                        <p className="text-xs text-[var(--text-muted)]">
                          {new Date(e.at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                      <p className="font-mono text-sm">₹{fmt(e.amount)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-5" data-testid="dashboard-ask">
              <p className="ft-overline">ASK RAYFUEL</p>
              <h3 className="font-display text-xl font-bold">Instant answers</h3>
              <p className="mt-2 text-sm text-[var(--text-muted)]">
                Voice or text — grounded in your data. Every answer cites the records used.
              </p>
              <button
                onClick={() => nav("/insights")}
                data-testid="dashboard-ask-btn"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF5A1F] hover:bg-[#E64A0F] text-white text-sm font-semibold"
              >
                <MessageSquare size={14} /> Open Ask RayFuel
              </button>
              <p className="mt-3 text-xs text-[var(--text-muted)] flex items-center gap-1"><Sparkles size={10}/> Try: "Why did my Health Score change?"</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function PrimaryLogRow() {
  const nav = useNavigate();
  const { hasPerm } = useAuth();
  const opts = [
    { key: "add_fuel",       to: "/new",             icon: Fuel,   label: "Fuel",     color: "#FF5A1F" },
    { key: "add_charging",   to: "/new/charging",    icon: Zap,    label: "Charge",   color: "#22D3EE" },
    { key: "log_service",    to: "/log/service",     icon: Wrench, label: "Service",  color: "#FBBF24" },
    { key: "upload_invoice", to: "/log/upload-bill", icon: ClipboardList, label: "Bill", color: "#F472B6" },
  ].filter((o) => hasPerm(o.key));
  return (
    <div className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-5 flex flex-wrap items-center gap-3" data-testid="dashboard-primary-log">
      <button onClick={() => nav("/add")} className="flex items-center gap-2 px-5 py-3 rounded-lg bg-[#FF5A1F] hover:bg-[#E64A0F] text-white text-sm font-bold" data-testid="dashboard-log-btn">
        <Plus size={18} /> Log
      </button>
      <div className="text-sm text-[var(--text-muted)]">Quick add:</div>
      {opts.map((o) => {
        const Icon = o.icon;
        return (
          <button key={o.key} onClick={() => nav(o.to)} data-testid={`quick-${o.key}`}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[var(--border-line)] hover:bg-[var(--hover)] text-xs">
            <Icon size={13} style={{ color: o.color }} /> {o.label}
          </button>
        );
      })}
    </div>
  );
}

function DriverPrimaryLog() {
  const nav = useNavigate();
  const { hasPerm } = useAuth();
  const primary = [
    { key: "add_fuel",       to: "/new",             icon: Fuel,   label: "Log Fuel",     color: "#FF5A1F" },
    { key: "add_charging",   to: "/new/charging",    icon: Zap,    label: "Log Charging", color: "#22D3EE" },
    { key: "log_service",    to: "/log/service",     icon: Wrench, label: "Log Service",  color: "#FBBF24" },
    { key: "upload_invoice", to: "/log/upload-bill", icon: ClipboardList, label: "Upload Bill", color: "#F472B6" },
  ].filter((o) => hasPerm(o.key));
  return (
    <div className="rounded-2xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-6" data-testid="dashboard-driver-primary">
      <div className="ft-overline">READY WHEN YOU ARE</div>
      <h2 className="font-display text-3xl font-black tracking-tighter mt-1">What did you do today?</h2>
      <p className="mt-2 text-sm text-[var(--text-muted)]">Tap what you want to log. Only your assigned vehicles will appear.</p>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {primary.map((o) => {
          const Icon = o.icon;
          return (
            <button key={o.key} onClick={() => nav(o.to)} data-testid={`driver-primary-${o.key}`}
              className="flex items-center gap-3 border border-[var(--border-line)] rounded-xl p-4 hover:bg-[var(--hover)] text-left">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: o.color, color: "#000" }}>
                <Icon size={22} strokeWidth={2.4} />
              </div>
              <span className="font-semibold">{o.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, hint, intent = "default", testid }) {
  const border = intent === "danger" ? "border-red-500/40" : "border-[var(--border-line)]";
  return (
    <div className={`rounded-xl border ${border} bg-[var(--bg-surface)] p-4`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="ft-overline">{label}</p>
        <Icon size={14} className="text-[var(--text-muted)]" />
      </div>
      <p className="font-display text-3xl font-black tracking-tight mt-1">{value}</p>
      <p className="text-xs text-[var(--text-muted)] mt-1">{hint}</p>
    </div>
  );
}

function ExpenseCard({ label, icon: Icon, amount, tone, testid }) {
  const c = tone === "orange" ? "#FF5A1F" : tone === "red" ? "#EF4444" : tone === "amber" ? "#FBBF24" : "#A3A3A3";
  return (
    <div className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-4 flex items-center gap-3" data-testid={testid}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${c}22` }}>
        <Icon size={18} style={{ color: c }} />
      </div>
      <div className="flex-1">
        <p className="text-xs text-[var(--text-muted)]">{label}</p>
        <p className="font-display text-xl font-black">₹{fmt(amount)}</p>
      </div>
    </div>
  );
}

function EventIcon({ kind }) {
  const map = {
    fuel:   { c: "#FF5A1F", i: Fuel },
    charge: { c: "#22D3EE", i: Zap },
    service:{ c: "#FBBF24", i: Wrench },
    repair: { c: "#EF4444", i: ShieldCheck },
  };
  const it = map[kind] || { c: "#A3A3A3", i: Car };
  const Icon = it.i;
  return (
    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${it.c}22` }}>
      <Icon size={14} style={{ color: it.c }} />
    </div>
  );
}
